/** Binary framing shared by WebTransport control streams and snapshot datagrams. */
export const MAX_MESSAGE_BYTES = 1_048_576;
export function encodeFrame(payload: Uint8Array): Uint8Array {
  const frame = new Uint8Array(4 + payload.length);
  new DataView(frame.buffer).setUint32(0, payload.length, true);
  frame.set(payload, 4);
  return frame;
}
export class FrameDecoder {
  private buffered = new Uint8Array(0);
  push(chunk: Uint8Array): Uint8Array[] {
    if (this.buffered.length + chunk.length > MAX_MESSAGE_BYTES * 2)
      throw new Error("Control stream buffer exceeded its limit");
    const joined = new Uint8Array(this.buffered.length + chunk.length);
    joined.set(this.buffered);
    joined.set(chunk, this.buffered.length);
    const frames: Uint8Array[] = [];
    let offset = 0;
    while (joined.length - offset >= 4) {
      const size = new DataView(joined.buffer, offset, 4).getUint32(0, true);
      if (!size || size > MAX_MESSAGE_BYTES)
        throw new Error("Invalid control message size");
      if (joined.length - offset < 4 + size) break;
      frames.push(joined.slice(offset + 4, offset + 4 + size));
      offset += 4 + size;
    }
    this.buffered = joined.slice(offset);
    return frames;
  }
}
interface Assembly {
  created: number;
  count: number;
  total: number;
  parts: Map<number, Uint8Array>;
  received: number;
}
export class DatagramAssembler {
  private pending = new Map<number, Assembly>();
  private latest = -1;
  push(packet: Uint8Array, now = performance.now()): Uint8Array | null {
    for (const [serial, a] of this.pending)
      if (now - a.created > 250) this.pending.delete(serial);
    if (
      packet.length < 16 ||
      packet[0] !== 0x57 ||
      packet[1] !== 0x5a ||
      packet[2] !== 1 ||
      packet[3] !== 0
    )
      return null;
    const view = new DataView(
      packet.buffer,
      packet.byteOffset,
      packet.byteLength,
    );
    const serial = view.getUint32(4, true),
      index = view.getUint16(8, true),
      count = view.getUint16(10, true),
      total = view.getUint32(12, true);
    if (
      serial <= this.latest ||
      !count ||
      count > 64 ||
      index >= count ||
      !total ||
      total > 262144 ||
      packet.length === 16
    )
      return null;
    let a = this.pending.get(serial);
    if (!a) {
      if (this.pending.size >= 4)
        this.pending.delete(this.pending.keys().next().value!);
      a = { created: now, count, total, parts: new Map(), received: 0 };
      this.pending.set(serial, a);
    }
    if (a.count !== count || a.total !== total) {
      this.pending.delete(serial);
      return null;
    }
    if (a.parts.has(index)) return null;
    const payload = packet.slice(16);
    a.parts.set(index, payload);
    a.received += payload.length;
    if (a.received > total) {
      this.pending.delete(serial);
      return null;
    }
    if (a.parts.size !== count) return null;
    this.pending.delete(serial);
    if (a.received !== total) return null;
    const result = new Uint8Array(total);
    let offset = 0;
    for (let i = 0; i < count; i++) {
      const part = a.parts.get(i)!;
      result.set(part, offset);
      offset += part.length;
    }
    this.latest = serial;
    for (const id of this.pending.keys())
      if (id <= serial) this.pending.delete(id);
    return result;
  }
}
